/**
 * Text Component - Base typography component
 */

import React from 'react';
import { Text as RNText, TextProps as RNTextProps, StyleSheet } from 'react-native';
import { colors, fontSizes, fontWeights } from '../theme/tokens';

export type TextVariant = 'h1' | 'h2' | 'h3' | 'body' | 'caption' | 'small';
export type TextColor = 'primary' | 'secondary' | 'tertiary' | 'success' | 'error' | 'warning';

interface TextProps extends Omit<RNTextProps, 'style'> {
  variant?: TextVariant;
  color?: TextColor;
  bold?: boolean;
  italic?: boolean;
  align?: 'left' | 'center' | 'right' | 'justify';
  style?: any;
}

const getVariantStyle = (variant: TextVariant) => {
  switch (variant) {
    case 'h1':
      return {
        fontSize: fontSizes['4xl'],
        fontWeight: fontWeights.bold,
        lineHeight: fontSizes['4xl'] * 1.2,
      };
    case 'h2':
      return {
        fontSize: fontSizes['3xl'],
        fontWeight: fontWeights.bold,
        lineHeight: fontSizes['3xl'] * 1.2,
      };
    case 'h3':
      return {
        fontSize: fontSizes['2xl'],
        fontWeight: fontWeights.semibold,
        lineHeight: fontSizes['2xl'] * 1.2,
      };
    case 'body':
      return {
        fontSize: fontSizes.base,
        fontWeight: fontWeights.normal,
        lineHeight: fontSizes.base * 1.5,
      };
    case 'caption':
      return {
        fontSize: fontSizes.sm,
        fontWeight: fontWeights.normal,
        lineHeight: fontSizes.sm * 1.5,
      };
    case 'small':
      return {
        fontSize: fontSizes.xs,
        fontWeight: fontWeights.normal,
        lineHeight: fontSizes.xs * 1.5,
      };
    default:
      return {
        fontSize: fontSizes.base,
        fontWeight: fontWeights.normal,
      };
  }
};

const getColorValue = (color: TextColor): string => {
  switch (color) {
    case 'primary':
      return colors.primary;
    case 'secondary':
      return colors.textSecondary;
    case 'tertiary':
      return colors.textTertiary;
    case 'success':
      return colors.success;
    case 'error':
      return colors.error;
    case 'warning':
      return colors.warning;
    default:
      return colors.gray9;
  }
};

/**
 * Text Component
 */
export const Text: React.FC<TextProps> = ({
  variant = 'body',
  color = 'primary',
  bold = false,
  italic = false,
  align = 'left',
  style,
  children,
  ...props
}) => {
  const baseStyle = getVariantStyle(variant);
  const textColor = getColorValue(color);

  const computedStyle = {
    ...baseStyle,
    color: textColor,
    fontWeight: bold ? fontWeights.bold : baseStyle.fontWeight,
    fontStyle: italic ? 'italic' : 'normal',
    textAlign: align,
  };

  return (
    <RNText style={[computedStyle, style]} {...props}>
      {children}
    </RNText>
  );
};

